import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Routes, Route } from "react-router-dom";
import {
    Page,
    PageHeader,
    PageLoader,
    PopupYesNo,
    PageNav,
    NavButton,
    PageActions,
    Action,
    Image,
    Toast
} from "@tv2no/ui-react";
import Copy from "./parts/Copy";
import Details from "./parts/Details";
import Persons from "./parts/Persons";
import { getPersonList, deletePersonList } from "../../api/services/personList";
import { getPersonListEntries } from "../../api/services/personListEntry";
import { getPersonCategoryList } from "../../api/services/personListCategory";
import { getTaxYearList } from "../../api/services/taxYear";

export const PersonListEdit = () => {
    const { listId } = useParams();
    const navigate = useNavigate();

    const [personList, setPersonList] = useState(null);
    const [showCopyPrompt, setShowCopyPrompt] = useState(false);
    const [showDeletePrompt, setShowDeletePrompt] = useState(false);
    const [busyDeleting, setBusyDeleting] = useState(false);
    const [personListCategories, setPersonListCategories] = useState(null);
    const [yearList, setYearList] = useState(null);
    const [listEntries, setListEntries] = useState(null);

    /**
     * Fetch tax year list
     * @private
     */
    const fetchTaxYearList = () => getTaxYearList().then((res) => setYearList(res));

    /**
     * Fetch person list categories
     * @private
     */
    const fetchPersonListCategories = () =>
        getPersonCategoryList().then((res) => setPersonListCategories(res));

    /**
     * Fetch person list
     * @private
     */
    const fetchPersonList = useCallback(
        (callback) => {
            getPersonList(listId).then((list) => {
                setPersonList(list);
                callback && callback();
            });
        },
        [listId]
    );

    /**
     * Fetch list entries
     * @private
     */
    const fetchPersonListEntries = useCallback(
        (callback) =>
            getPersonListEntries(listId).then((res) => {
                setListEntries(res);
                callback && callback();
            }),
        [listId]
    );

    /**
     * Delete list
     * @private
     */
    const handleDeleteList = () => {
        // Busy on
        setBusyDeleting(true);

        // Send request
        deletePersonList(personList.id).then(() => {
            Toast.info("Listen ble slettet");
            navigate(`/lists/${personList.taxYear}`, { replace: true });
        });
    };

    useEffect(() => {
        fetchTaxYearList();
        fetchPersonList();
        fetchPersonListEntries();
        fetchPersonListCategories();
    }, [fetchPersonList, fetchPersonListEntries]);

    return !personList ? (
        <PageLoader />
    ) : (
        <Page>
            <PageHeader
                title={personList?.name}
                subtitle={`${personList.category?.name} ${personList?.taxYear}`}
                image={<Image src={personList?.thumbnail?.url} scale="fill" />}
            />
            <PageNav>
                <NavButton icon="edit" label="Detaljer" link={`/lists/entry/${listId}`} />
                <NavButton icon="users" label="Personer" link={`/lists/entry/${listId}/persons`} />
            </PageNav>
            <PageActions title="Handlinger">
                <Action icon="copy" label="Kopier listen" onClick={() => setShowCopyPrompt(true)} />
                <Action
                    icon="trash"
                    iconColor="error"
                    label="Slett listen"
                    onClick={() => setShowDeletePrompt(true)}
                />
                <Action icon="info" label="Hjelp">
                    Fyll inn detaljene og last opp ett miniatyrbilde og ett fullskjermsbilde for den
                    kuraterte listen.
                    <br />
                    <br />
                    Hvis du ønsker å manuelt bestemme rekkefølgen på personene i listen velger du
                    "rangering" i nedtrekksmenyen under "sortering".
                </Action>
            </PageActions>
            <Routes>
                <Route
                    path="/"
                    element={
                        <Details
                            personList={personList}
                            personListCategories={personListCategories}
                            setPersonList={setPersonList}
                            fetchPersonList={fetchPersonList}
                        />
                    }
                />
                <Route
                    path="/persons"
                    element={
                        <Persons
                            listEntries={listEntries}
                            yearList={yearList}
                            personList={personList}
                            fetchPersonListEntries={fetchPersonListEntries}
                        />
                    }
                />
            </Routes>

            {/* Copy list prompt */}
            {showCopyPrompt ? (
                <Copy
                    personList={personList}
                    yearList={yearList}
                    setShowCopyPrompt={(val) => setShowCopyPrompt(val)}
                />
            ) : null}

            {/* Delete list confirmation */}
            {showDeletePrompt ? (
                <PopupYesNo
                    title="Slett listen?"
                    busy={busyDeleting}
                    onYes={handleDeleteList}
                    onNo={() => setShowDeletePrompt(false)}
                >
                    Er du sikker på at du vil slette denne listen?
                </PopupYesNo>
            ) : null}
        </Page>
    );
};
